import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useRecordList } from '@/features/shared/hooks/useRecordList';
import { usePondNames } from '@/features/shared/hooks/usePondNames';
import { waterQualityApi, type WaterQualityListParams } from '../api/waterQualityApi';
import { Card } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { LoadMoreButton } from '@/shared/components/ui/LoadMoreButton';
import { SectionLoading } from '@/shared/components/ui/LoadingSpinner';
import { formatDateTime } from '@/shared/lib/utils';
import { Image as ImageIcon, Filter, X } from 'lucide-react';
import type { WaterQualityResponse } from '@/shared/types/api';

type WaterQualityFilter = 'has_issues' | 'microcystis';

const filterLabels: Record<WaterQualityFilter, string> = {
  has_issues: 'Parameter Issues',
  microcystis: 'Microcystis Present',
};

interface WaterQualityRecordsTabProps {
  farmId: string;
  selectedPondId: string | null;
  onRecordClick?: (record: WaterQualityResponse) => void;
}

export const WaterQualityRecordsTab: React.FC<WaterQualityRecordsTabProps> = ({
  farmId,
  selectedPondId,
  onRecordClick,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pondNames = usePondNames(farmId);

  // Read filter params from URL
  const filter = searchParams.get('filter') as WaterQualityFilter | null;
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');

  const clearFilter = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('filter');
    newParams.delete('startDate');
    newParams.delete('endDate');
    setSearchParams(newParams, { replace: true });
  };

  const { records, pagination, isLoading, isError } = useRecordList<WaterQualityResponse, WaterQualityListParams>({
    farmId,
    params: {
      pondId: selectedPondId,
      limit: 20,
      sortOrder: 'desc',
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      filter: filter || undefined,
    },
    queryKey: ['water-quality', 'records', selectedPondId ?? 'all', filter ?? 'none', startDate ?? '', endDate ?? ''],
    fetchFn: waterQualityApi.listByFarm,
  });

  if (isLoading) {
    return <SectionLoading message="Loading water quality records..." />;
  }

  if (isError) {
    return (
      <Card>
        <div className="text-center py-12">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-red-100 border border-red-200/50 mb-4">
            <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="font-roboto text-sm font-semibold text-gray-900">
            Failed to load records
          </h3>
          <p className="font-roboto mt-1 text-sm text-gray-600">
            There was an error loading water quality records. Please try again.
          </p>
        </div>
      </Card>
    );
  }

  if (records.length === 0) {
    return (
      <div className="space-y-4">
        {/* Filter Indicator */}
        {filter && (
          <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200/50 rounded-lg">
            <Filter className="h-4 w-4 text-blue-600" />
            <span className="text-sm text-blue-800">
              Showing records with: <strong>{filterLabels[filter]}</strong>
              {startDate && endDate && (
                <span className="text-blue-600 ml-1">
                  ({new Date(startDate).toLocaleDateString()} - {new Date(endDate).toLocaleDateString()})
                </span>
              )}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilter}
              className="ml-auto h-6 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100"
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          </div>
        )}
        <Card>
          <div className="text-center py-12">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100 border border-gray-200/50 mb-4">
              <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="font-roboto text-sm font-semibold text-gray-900">
              No water quality records
            </h3>
            <p className="font-roboto mt-1 text-sm text-gray-600">
              {filter
                ? `No records found with ${filterLabels[filter]}`
                : selectedPondId
                ? 'No records found for the selected pond.'
                : 'Select a pond above to view water quality records.'}
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter Indicator */}
      {filter && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200/50 rounded-lg">
          <Filter className="h-4 w-4 text-blue-600" />
          <span className="text-sm text-blue-800">
            Showing records with: <strong>{filterLabels[filter]}</strong>
            {startDate && endDate && (
              <span className="text-blue-600 ml-1">
                ({new Date(startDate).toLocaleDateString()} - {new Date(endDate).toLocaleDateString()})
              </span>
            )}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilter}
            className="ml-auto h-6 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100"
          >
            <X className="h-3 w-3 mr-1" />
            Clear
          </Button>
        </div>
      )}

      {records.map((record) => (
        <Card
          key={record.id}
          className={`transition-all duration-200 ${
            onRecordClick ? 'cursor-pointer hover:shadow-lg hover:border-primary-300/50' : ''
          }`}
          onClick={() => onRecordClick?.(record)}
        >
          <div className="space-y-4">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-roboto font-semibold text-gray-900">
                    Water Quality Test
                  </h3>
                  {record.imageIds && record.imageIds.length > 0 && (
                    <Badge variant="info" className="flex items-center gap-1">
                      <ImageIcon className="h-3 w-3" />
                      {record.imageIds.length}
                    </Badge>
                  )}
                </div>
                <p className="font-roboto text-sm text-gray-600">
                  {!selectedPondId && record.pondId && `${pondNames[record.pondId] || `Pond ${record.pondId.slice(-6)}`} • `}{formatDateTime(record.testedAt)}
                </p>
              </div>
              {/* Show pond badge if viewing all ponds */}
              {!selectedPondId && record.pondId && (
                <Badge className="bg-primary-50 text-primary-700 border-primary-200/50">
                  {pondNames[record.pondId] || `Pond ${record.pondId.slice(-6)}`}
                </Badge>
              )}
            </div>

            {/* Test Parameters Grid */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {record.dissolvedOxygen !== undefined && record.dissolvedOxygen !== null && (
                <div>
                  <p className="font-roboto text-xs text-gray-500">Dissolved Oxygen</p>
                  <p className="font-roboto text-lg font-semibold font-mono text-gray-900">
                    {record.dissolvedOxygen} mg/L
                  </p>
                </div>
              )}
              {record.ph !== undefined && record.ph !== null && (
                <div>
                  <p className="font-roboto text-xs text-gray-500">pH</p>
                  <p className="font-roboto text-lg font-semibold font-mono text-gray-900">
                    {record.ph}
                  </p>
                </div>
              )}
              {record.temperature !== undefined && record.temperature !== null && (
                <div>
                  <p className="font-roboto text-xs text-gray-500">Temperature</p>
                  <p className="font-roboto text-lg font-semibold font-mono text-gray-900">
                    {record.temperature}°C
                  </p>
                </div>
              )}
              {record.salinity !== undefined && record.salinity !== null && (
                <div>
                  <p className="font-roboto text-xs text-gray-500">Salinity</p>
                  <p className="font-roboto text-lg font-semibold font-mono text-gray-900">
                    {record.salinity} ppt
                  </p>
                </div>
              )}
              {record.ammonia !== undefined && record.ammonia !== null && (
                <div>
                  <p className="font-roboto text-xs text-gray-500">Ammonia</p>
                  <p className="font-roboto text-lg font-semibold font-mono text-gray-900">
                    {record.ammonia} mg/L
                  </p>
                </div>
              )}
              {record.nitrite !== undefined && record.nitrite !== null && (
                <div>
                  <p className="font-roboto text-xs text-gray-500">Nitrite</p>
                  <p className="font-roboto text-lg font-semibold font-mono text-gray-900">
                    {record.nitrite} mg/L
                  </p>
                </div>
              )}
              {record.nitrate !== undefined && record.nitrate !== null && (
                <div>
                  <p className="font-roboto text-xs text-gray-500">Nitrate</p>
                  <p className="font-roboto text-lg font-semibold font-mono text-gray-900">
                    {record.nitrate} mg/L
                  </p>
                </div>
              )}
              {record.turbidity !== undefined && record.turbidity !== null && (
                <div>
                  <p className="font-roboto text-xs text-gray-500">Turbidity</p>
                  <p className="font-roboto text-lg font-semibold font-mono text-gray-900">
                    {record.turbidity} NTU
                  </p>
                </div>
              )}
            </div>

            {/* Notes */}
            {record.notes && (
              <div className="pt-4 border-t border-slate-200/50">
                <p className="font-roboto text-xs text-gray-500 mb-1">Notes</p>
                <p className="font-roboto text-sm text-gray-900">{record.notes}</p>
              </div>
            )}
          </div>
        </Card>
      ))}

      {/* Load More */}
      {pagination?.hasNext && (
        <LoadMoreButton
          hasMore={pagination.hasNext}
          isLoading={isLoading}
          onClick={() => {
            // TODO: Implement load more with cursor
          }}
        />
      )}
    </div>
  );
};
